/**
 * BETA INSIGHTS — the pilot telemetry presentation core.
 *
 * Pure and dependency-free. Reads `pilot-telemetry-1` (backend #22,
 * `services/pilot_telemetry.py`) and renders exactly what it says.
 *
 * THE THREE RULES THIS SURFACE EXISTS UNDER
 *
 * 1. NO INVENTED VALUE. There is no cost field, no rate field and no
 *    baseline-effort field anywhere in the payload, so ROI, money saved,
 *    hours saved and productivity improvement cannot be computed from it —
 *    only fabricated. `decisions.responseTimeHours` is how long a HUMAN took
 *    to decide; captioning it as hours saved inverts its meaning.
 *
 * 2. NULL IS UNKNOWN, NEVER ZERO. An instance born BLOCKED emits no
 *    transition, so its age is genuinely unknown — and on a fresh pilot
 *    project MOST blocked instances look like this. Rendering those as 0
 *    would report a fleet of brand-new blockers.
 *
 * 3. ALL METRICS ARE ALL-TIME. There is no date filter in any query.
 *    `dataDate` is when the snapshot was taken, not the start or end of a
 *    window, so no panel here may say "this week".
 */

export const TELEMETRY_CONTRACT_VERSION = 'pilot-telemetry-1';

/** The three readiness actions, so a zero renders as 0 rather than vanishing. */
export const READINESS_ACTIONS = [
  'gates_reevaluated',
  'ers_recalculated',
  'schedule_ers_recalculated',
];

const READINESS_ACTION_LABEL = {
  gates_reevaluated: 'Gates re-evaluated',
  ers_recalculated: 'Readiness recalculated',
  schedule_ers_recalculated: 'Schedule impact recalculated',
};

const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/**
 * One rendered metric. `value: null` means the backend could not establish
 * it and the caller renders "unknown" — never a zero, never a blank.
 */
const metric = (label, value, { unit = null, basis = null, caveat = null } = {}) => ({
  label,
  value,
  unit,
  basis,
  caveat,
  known: value !== null && value !== undefined,
});

// ---------------------------------------------------------------------------
// Blockers
// ---------------------------------------------------------------------------

/**
 * `advanceWarningDays === null` has THREE causes, and only one of them is bad
 * news:
 *
 *   plannedStart === null                  → genuinely UNKNOWN, there is no
 *                                            baseline to measure against
 *   detectedAt === null                    → also UNKNOWN; the backend guards
 *                                            on `planned_start is not None
 *                                            and detected_at is not None`, so
 *                                            a detection with no recorded
 *                                            timestamp is neither measured
 *                                            NOR counted in
 *                                            `lateDetectionCount`
 *   both present, warning still null       → the detection was AT OR AFTER
 *                                            the planned start: a KNOWN BAD
 *                                            fact, and the only one of the
 *                                            three that is counted late
 *
 * Rendering a late detection as "unknown" would hide the worst news in the
 * payload behind the same grey label as missing data — and calling an
 * untimestamped detection late would invent bad news the backend did not
 * report. Both halves matter, so both are checked.
 */
export const presentDetection = (row) => {
  const plannedStart = row?.plannedStart || null;
  const detectedAt = row?.detectedAt || null;
  const advance = num(row?.advanceWarningDays);
  const late = plannedStart !== null && detectedAt !== null && advance === null;

  return {
    instanceId: row?.instanceId || null,
    detectedAt,
    plannedStart,
    advanceWarningDays: advance,
    trigger: row?.trigger || null,
    late,
    warning: late
      ? { label: 'Detected late', tone: 'bad', detail: 'Found at or after the work was due to start.' }
      : advance !== null
        ? {
            label: `${advance} ${advance === 1 ? 'day' : 'days'} ahead`,
            tone: advance > 0 ? 'good' : 'attention',
            detail: 'Found before the work was due to start.',
          }
        : {
            label: 'Unknown',
            tone: 'unknown',
            detail:
              plannedStart === null
                ? 'This activity has no planned start, so there is nothing to measure warning against.'
                : 'This detection has no recorded time, so how much warning it gave cannot be measured.',
          },
  };
};

/**
 * Blocker age is null for any instance that was never observed transitioning
 * INTO blocked — which includes every instance that was created blocked and
 * has stayed that way. That is the common case on a new project.
 */
export const presentBlockedInstance = (row) => {
  const age = num(row?.blockerAgeDays);
  return {
    instanceId: row?.instanceId || null,
    instanceName: row?.instanceName || null,
    blockedSince: row?.blockedSince || null,
    blockerAgeDays: age,
    blockingReason: row?.blockingReason || null,
    age:
      age !== null
        ? { label: `${age} ${age === 1 ? 'day' : 'days'}`, tone: age > 14 ? 'bad' : 'attention' }
        : {
            label: 'Unknown',
            tone: 'unknown',
            detail:
              'This activity has been blocked since it was created, so there is no transition to measure from.',
          },
  };
};

export const presentBlockers = (payload) => {
  const block = payload?.blockers;
  if (!block) return null;
  const detections = (block.detections || []).map(presentDetection);
  const currentlyBlocked = (block.currentlyBlocked || []).map(presentBlockedInstance);
  const detectedCount = num(block.detectedCount) ?? 0;

  return {
    basis: block.basis || null,
    metrics: [
      metric('Blockers detected', detectedCount, {
        caveat:
          'Counts events, not activities: one that was blocked, cleared and blocked again counts twice.',
      }),
      metric('Detected late', num(block.lateDetectionCount) ?? 0, {
        caveat: 'Found at or after the work was due to start.',
      }),
      metric('Cleared', num(block.clearedCount) ?? 0),
      metric('Blocked right now', num(block.currentlyBlockedCount) ?? 0),
    ],
    detections,
    currentlyBlocked,
    /**
     * Lists are capped at 200 with NO truncation flag — the only way to know
     * is to compare a list against its sibling count, so that is done here.
     */
    detectionsTruncated: detections.length < detectedCount,
    blockedTruncated: currentlyBlocked.length < (num(block.currentlyBlockedCount) ?? 0),
    /** Said out loud, because detected − cleared is NOT currently blocked. */
    countsAreNotArithmetic: true,
  };
};

/**
 * Whether the counts themselves are a floor.
 *
 * The audit load is capped at 10,000 rows and the backend says so IN PROSE,
 * inside `missingInformation`, with no boolean to read. A panel that promises
 * "the counts cover everything" can therefore be wrong — this looks for the
 * backend's own sentence rather than asserting completeness on its behalf.
 */
export const auditLoadCapped = (payload) =>
  (payload?.missingInformation || []).some((note) => String(note).includes('row load cap'));

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

/**
 * `byAction` is SPARSE — an action with no events is absent from the dict
 * rather than present as 0. Iterating the returned keys would silently drop
 * the fact that a whole class of recalculation never ran.
 */
export const presentReadiness = (payload) => {
  const readiness = payload?.readiness;
  if (!readiness) return null;
  const byAction = readiness.byAction || {};
  return {
    basis: readiness.basis || null,
    calculationCount: num(readiness.calculationCount) ?? 0,
    lastCalculatedAt: readiness.lastCalculatedAt || null,
    actions: READINESS_ACTIONS.map((action) => ({
      action,
      label: READINESS_ACTION_LABEL[action] || action.replace(/_/g, ' '),
      count: num(byAction[action]) ?? 0,
    })),
  };
};

// ---------------------------------------------------------------------------
// Unknowns
// ---------------------------------------------------------------------------

/**
 * `unresolvedRate` is null — not 0 — when there are no active requirements.
 * A 0% badge over an empty register would be a lie about a project nobody
 * has written requirements for yet.
 */
export const presentUnknowns = (payload) => {
  const unknowns = payload?.unknowns;
  if (!unknowns) return null;
  const rate = num(unknowns.unresolvedRate);
  const active = num(unknowns.activeRequirementCount) ?? 0;

  return {
    basis: unknowns.basis || null,
    denominator: unknowns.denominator || null,
    metrics: [
      metric('Unknowns resolved', num(unknowns.resolvedCount) ?? 0, {
        caveat:
          'Counts elevations out of weak provenance only. Re-verifying one weak status as another does not count.',
      }),
      metric('Verification events', num(unknowns.verificationEventCount) ?? 0),
      metric('Still unresolved', num(unknowns.currentUnresolvedCount) ?? 0),
      metric('Active requirements', active),
    ],
    unresolvedRate: rate,
    ratePercent: rate === null ? null : Math.round(rate * 1000) / 10,
    rateUnknownBecause:
      rate === null && active === 0
        ? 'This project has no active requirements, so the rate has no denominator.'
        : null,
  };
};

// ---------------------------------------------------------------------------
// Decisions
// ---------------------------------------------------------------------------

/**
 * Response time is HUMAN LATENCY: how long a person took to answer a
 * proposal. It is not a saving, not a cycle time, and not a throughput.
 *
 * The aggregate blends schedule and award proposals — only `responses[].kind`
 * separates them and there is no per-kind latency breakdown — so the label
 * says "proposals", not one or the other.
 */
export const presentDecisions = (payload) => {
  const decisions = payload?.decisions;
  if (!decisions) return null;
  const rt = decisions.responseTimeHours || {};
  const responses = Array.isArray(decisions.responses) ? decisions.responses : [];
  const resolved = num(decisions.resolvedCount) ?? 0;
  const acceptance = decisions.recoveryAcceptance || {};
  const byKind = acceptance.byProposalKind || {};

  return {
    basis: decisions.basis || null,
    metrics: [
      metric('Decisions required', num(decisions.requiredCount) ?? 0),
      metric('Still open', num(decisions.openCount) ?? 0),
      metric('Decided', resolved),
      metric('Average time to decide', num(rt.mean), {
        unit: 'hours',
        caveat:
          num(rt.mean) === null
            ? 'No decision here has both a recorded request and a recorded answer yet, so there is nothing to average.'
            : `How long a person took to answer, over ${num(rt.count) ?? 0} measured ${
                (num(rt.count) ?? 0) === 1 ? 'decision' : 'decisions'
              } across schedule and award proposals together. It is not time saved.`,
      }),
      metric('Longest wait', num(rt.max), { unit: 'hours' }),
    ],
    measuredCount: num(rt.count) ?? 0,
    responses: responses.map((row) => ({
      proposalId: row?.proposalId || null,
      kind: row?.kind || null,
      state: row?.state || null,
      createdAt: row?.createdAt || null,
      decidedAt: row?.decidedAt || null,
      responseHours: num(row?.responseHours),
    })),
    responsesTruncated: responses.length < resolved,
    /**
     * Schedule proposals only. Award decisions use different audit actions
     * and land in the commercial funnel instead, so presenting this as "all
     * recovery decisions" would understate award activity.
     */
    recoveryAcceptance: {
      approved: num(acceptance.approved) ?? 0,
      rejected: num(acceptance.rejected) ?? 0,
      byKind: Object.keys(byKind).map((kind) => ({
        kind,
        approved: num(byKind[kind]?.approved) ?? 0,
        rejected: num(byKind[kind]?.rejected) ?? 0,
        open: num(byKind[kind]?.open) ?? 0,
      })),
      scheduleOnly: true,
    },
  };
};

// ---------------------------------------------------------------------------
// Commercial funnel
// ---------------------------------------------------------------------------

/**
 * `quoteLeveledCount` is literally the same integer as
 * `awardRecommendationCreatedCount` — a leveling run that produced no award
 * proposal leaves no durable record, so the backend counts proposals and says
 * so. Rendering them as two funnel stages would imply a conversion rate that
 * is structurally always 100%.
 *
 * They are therefore presented as ONE stage, with the substitution named.
 */
export const presentCommercial = (payload) => {
  const commercial = payload?.commercial;
  if (!commercial) return null;
  const leveled = num(commercial.quoteLeveledCount);
  const recommended = num(commercial.awardRecommendationCreatedCount);

  return {
    basis: commercial.basis || null,
    funnel: [
      {
        label: 'RFQs created',
        value: num(commercial.rfqCreatedCount) ?? 0,
        caveat: 'Counts all three ways a bid package is born.',
      },
      { label: 'Quotes received', value: num(commercial.quoteReceivedCount) ?? 0, caveat: null },
      {
        label: 'Award recommendations',
        value: recommended ?? 0,
        caveat:
          'Leveling is counted through award proposals: a leveling run with no proposal leaves no record, so these two are the same number by construction.',
      },
      {
        label: 'Recommendations reviewed',
        value: num(commercial.awardRecommendationReviewedCount) ?? 0,
        caveat: null,
      },
      {
        label: 'Providers assigned',
        value: num(commercial.providerAssignedCount) ?? 0,
        caveat:
          'Assigned through the award workflow. A package awarded directly is counted beside this, not in it.',
      },
      {
        label: 'Awarded directly',
        value: num(commercial.legacyAwardedCount) ?? 0,
        caveat: 'Awarded through procurement or the operator tool, without an award workflow.',
      },
    ],
    /**
     * The direct/operator award path. A package awarded through the
     * procurement route or the operator tool lands HERE and not in
     * `providerAssignedCount`, so dropping it would show a project that
     * awarded every package as having assigned nobody. It is a real funnel
     * outcome and is rendered as one.
     */
    legacyAwardedCount: num(commercial.legacyAwardedCount) ?? 0,
    /** True whenever the backend's structural equality holds, as it always does. */
    levelingIsProxiedByProposals: leveled === recommended,
  };
};

// ---------------------------------------------------------------------------
// Schedule impact
// ---------------------------------------------------------------------------

export const presentScheduleImpact = (payload) => {
  const impact = payload?.scheduleImpact;
  if (!impact) return null;
  const detections = Array.isArray(impact.detections) ? impact.detections : [];
  const observed = num(impact.impactDetectedCount) ?? 0;

  return {
    basis: impact.basis || null,
    metrics: [
      metric('Impact observations', observed, {
        caveat:
          'Counts recalculations that SAW an at-risk or infeasible verdict. One impact that persists is counted on every recalculation, not once.',
      }),
      metric('Project recalculations', num(impact.recalculationCount) ?? 0, {
        caveat:
          'Project-level recalculations only. The recalculations panel counts a different thing — it includes per-activity work — so the two figures are not comparable.',
      }),
      metric('Recovery proposals', num(impact.recoveryProposedCount) ?? 0),
    ],
    lastImpactAt: impact.lastImpactAt || null,
    detections: detections.map((row) => ({
      at: row?.at || null,
      classification: row?.classification || null,
      forecastFinish: row?.forecastFinish || null,
      approvedFinish: row?.approvedFinish || null,
      recoveryNeeded: typeof row?.recoveryNeeded === 'boolean' ? row.recoveryNeeded : null,
    })),
    detectionsTruncated: detections.length < observed,
  };
};

// ---------------------------------------------------------------------------
// The whole panel
// ---------------------------------------------------------------------------

/**
 * `missingInformation` is the honest-gaps channel and is NEVER empty on a
 * successful response — the quote-leveling substitution note is appended
 * unconditionally. So its presence is not news; its contents are.
 */
export const presentTelemetry = (payload) => {
  if (!payload) return null;
  return {
    contractVersion: payload.contractVersion || null,
    contractMatches: payload.contractVersion === TELEMETRY_CONTRACT_VERSION,
    projectId: payload.projectId || null,
    /** When the snapshot was taken. NOT the start or end of a window. */
    dataDate: payload.dataDate || null,
    allTime: true,
    blockers: presentBlockers(payload),
    readiness: presentReadiness(payload),
    unknowns: presentUnknowns(payload),
    decisions: presentDecisions(payload),
    commercial: presentCommercial(payload),
    scheduleImpact: presentScheduleImpact(payload),
    missingInformation: Array.isArray(payload.missingInformation)
      ? payload.missingInformation
      : [],
    readOnly: payload.readOnly === true,
    deterministic: payload.deterministic === true,
  };
};

/**
 * Every figure this surface may state, flattened, so a test can assert that
 * none of them is a fabricated value metric. Kept beside the presenters
 * rather than in the test file so it cannot drift away from them.
 */
export const FORBIDDEN_VALUE_CLAIMS = [
  'roi',
  'money saved',
  'cost saved',
  'savings',
  'hours saved',
  'time saved',
  'productivity',
  'efficiency gain',
  'return on investment',
];
